/**
 * components/platform/allowanceMeter.tsx
 *
 * SKPLATFORM-UICC-006 N-1 §6.1–6.3 / DB-006 S7a — the customer activity figure.
 *
 * ── WHY THIS IS NOT `Meter` FROM primitives.tsx ───────────────────────────
 *
 * `Meter` turns DANGER RED at 100%. This surface must not. At and above the
 * allowance the fill goes WARNING, because nothing is broken and nothing is
 * blocked: the meter never gates authentication, so a critical colour here
 * would be a false statement about the product.
 *
 * That single difference is the reason this is a separate component rather
 * than a prop on the existing one — a prop would eventually be forgotten, and
 * the failure mode of forgetting it is telling an operator their customers are
 * locked out when they are not.
 *
 * ── THE THREE THINGS THIS SURFACE MUST NEVER DO (U-D5) ────────────────────
 *
 * 1. Never suggest access is at risk. No "limit", "cap", "quota", "exceeded",
 *    "blocked", no lock icons, no red. The words used are *included*,
 *    *allowance*, *above your allowance*.
 * 2. Never invite record deletion as a remedy. There is no cleanup CTA here.
 *    The meter counts ACTIVITY rather than records precisely so that deleting
 *    data saves nothing; a UI that re-introduced the incentive would defeat
 *    the design it implements.
 * 3. Never show a price. Platform counts; SK Billing prices. Blocks, not
 *    currency. A currency figure here would be reading from the wrong service.
 *
 * ── FORM ──────────────────────────────────────────────────────────────────
 *
 * A single horizontal bar. Not a gauge, not a donut, not a pie — per the suite
 * chart rubric a single measure against a target is a bar or a bullet.
 */

import React from 'react';
import { View, Text } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { FS, FW } from '../../utils/platformTokens';

/** Words that must never appear on this surface. Exported so a test can assert it. */
export const BANNED_ALLOWANCE_WORDS = [
  'limit',
  'cap',
  'quota',
  'exceeded',
  'over-limit',
  'blocked',
  'suspended',
  'at risk',
  'users',
] as const;

export interface AllowanceMeterProps {
  activeCount: number;
  allowance: number;
  blockSize: number;
  blocksOver: number;
  /** e.g. "1 and 30 September" — the period, spelled out in the definition line. */
  periodLabel: string;
}

export const AllowanceMeter: React.FC<AllowanceMeterProps> = ({
  activeCount,
  allowance,
  blockSize,
  blocksOver,
  periodLabel,
}) => {
  const { theme } = useTheme();
  const c = theme.colors;

  const pct = allowance > 0 ? Math.round((activeCount / allowance) * 100) : 0;
  const atOrOver = allowance > 0 && activeCount >= allowance;
  const approaching = !atOrOver && pct >= 80;

  /**
   * Accent under 80%, warning at 80% and above — and warning ALSO at and above
   * 100%. There is deliberately no branch that returns a danger colour.
   */
  const fill = approaching || atOrOver ? c.warn : c.accent;

  /** The line beneath, which changes at each threshold. */
  const note = atOrOver
    ? `${blocksOver} ${blocksOver === 1 ? 'block' : 'blocks'} of ${blockSize.toLocaleString()} above your allowance. Additional customers are billed per block.`
    : approaching
      ? 'Approaching your included allowance.'
      : null;

  return (
    <View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 24 }}>
        <View>
          {/* Every number is labelled with its unit. Not "12,400" but
              "12,400 active customers" — U-D4, checklist #12. */}
          <Text style={{ fontSize: FS.sm, color: c.ink3 }}>Active customers this period</Text>
          <Text
            style={{
              fontSize: 30,
              fontWeight: FW.semibold,
              color: c.ink,
              fontFamily: theme.font.mono,
              marginTop: 2,
            }}
          >
            {activeCount.toLocaleString()}
          </Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={{ fontSize: FS.sm, color: c.ink3 }}>Included in your plan</Text>
          <Text
            style={{
              fontSize: 30,
              fontWeight: FW.semibold,
              color: c.ink3,
              fontFamily: theme.font.mono,
              marginTop: 2,
            }}
          >
            {allowance.toLocaleString()}
          </Text>
        </View>
      </View>

      {/* The bar. Capped at 100% width so an over-allowance figure does not
          overflow its track, while the note below states the real overage. */}
      <View
        style={{
          height: 10,
          borderRadius: 999,
          backgroundColor: c.paper3,
          overflow: 'hidden',
          marginTop: 14,
        }}
      >
        <View
          style={{
            width: `${Math.min(100, pct)}%`,
            height: '100%',
            backgroundColor: fill,
            borderRadius: 999,
          }}
        />
      </View>
      <Text
        style={{
          fontSize: FS.xs,
          color: c.ink3,
          marginTop: 6,
          fontFamily: theme.font.mono,
        }}
      >
        {pct}%
      </Text>

      {/*
        The definition of "active", INLINE and ALWAYS VISIBLE — never a
        tooltip. Somebody will dispute this number, and the screen should
        already have answered them.
      */}
      <Text style={{ fontSize: FS.xs, color: c.ink3, marginTop: 10, lineHeight: 18 }}>
        Active means a customer who signed in or transacted between {periodLabel}. Dormant
        customers are not counted.
      </Text>

      {note && (
        <Text style={{ fontSize: FS.sm, color: c.warn, marginTop: 10, fontWeight: FW.medium }}>
          {note}
        </Text>
      )}
    </View>
  );
};
